/**
 * Portal sign in (tracker item 45): /portal/login and the company landing
 * page /portal/:slug (the link in invoice emails). Same account store and
 * session as /login; a customer lands on /portal (homePathFor), anyone else
 * on their own home. Wrong password, unconfirmed email and unknown company
 * all read the same.
 */
import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MfaChallengeRequiredError, UserRole } from '../../api/types';
import { PortalPublicLayout, portalLinkClass } from './PortalPublicLayout';
import { InlineAlert } from './PortalStates';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { describePortalFailure, slugFromParam } from '../../utils/portal';
import { homePathFor } from '../../utils/permissions';
import * as storage from '../../utils/storage';

export function PortalLoginPage() {
  const { login, isLoading, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { slug: slugParam } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const fixedSlug = slugFromParam(slugParam) || slugFromParam(search.get('company') ?? undefined);
  const [tenantSlug, setTenantSlug] = useState(fixedSlug || storage.getTenantSlug() || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const verified = search.get('verified') === '1';

  // Already signed in as a customer: straight to the portal
  if (isAuthenticated && user?.role === UserRole.CUSTOMER_PORTAL) return <Navigate to="/portal" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const signedIn = await login(tenantSlug.trim().toLowerCase(), email.trim(), password);
      navigate(homePathFor(signedIn), { replace: true });
    } catch (cause) {
      // A staff login with two-step verification on (tracker item 63): the
      // code step lives on the staff sign-in page (the challenge is kept in AuthContext)
      if (cause instanceof MfaChallengeRequiredError) {
        navigate('/login', { replace: true });
        return;
      }
      const described = describePortalFailure(cause, 'Sign in failed');
      setError(described.kind === 'disabled' ? described.message : 'Invalid email or password, or the account is not confirmed yet.');
    } finally {
      setBusy(false);
    }
  }

  const input = inputClass + ' min-h-[44px] text-base';
  const registerHref = fixedSlug ? `/portal/${fixedSlug}/register` : '/portal/register';
  const forgotHref = fixedSlug ? `/portal/forgot-password?company=${fixedSlug}` : '/portal/forgot-password';
  return (
    <PortalPublicLayout title="Sign in" intro="See your invoices and visits, and pay online.">
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {verified && <InlineAlert kind="success">Your email is confirmed. Sign in with the password you just chose.</InlineAlert>}
        {!fixedSlug && (
          <div>
            <label htmlFor="portal-company" className={labelClass}>Company</label>
            <input id="portal-company" className={input} value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} disabled={busy || isLoading} autoCapitalize="none" required />
          </div>
        )}
        <div>
          <label htmlFor="portal-email" className={labelClass}>Email address</label>
          <input id="portal-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy || isLoading} autoComplete="username" required />
        </div>
        <div>
          <label htmlFor="portal-login-password" className={labelClass}>Password</label>
          <input id="portal-login-password" type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy || isLoading} autoComplete="current-password" required />
        </div>
        {error && <InlineAlert>{error}</InlineAlert>}
        <button type="submit" className={`${primaryButtonClass} w-full text-base`} disabled={busy || isLoading}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
      <div className="mt-4 flex flex-col">
        <Link to={forgotHref} className={portalLinkClass}>Forgot your password?</Link>
        <Link to={registerHref} className={portalLinkClass}>New here? Create your online account</Link>
        <Link to="/login" className="inline-flex min-h-[44px] items-center text-xs text-gray-500 hover:text-gray-700">Staff sign in</Link>
      </div>
    </PortalPublicLayout>
  );
}
